'use client';

import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Logger } from '@/core/utils/logger';
import { AlertCircle, RefreshCw, RotateCcw } from 'lucide-react';
import { useAppSelector } from '@/core/store';
import { selectBootSentryContext } from '@/core/store/slices/bootSlice';
import { config } from '@/config/config';

interface RouteErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * RouteError - Shared error component for Next.js App Router error.tsx files
 *
 * Features:
 * - Retry button (calls reset() to re-render the route segment)
 * - Hard reload button for catastrophic states
 * - Production-safe: shows message + digest, not full stack trace
 * - Sentry tagging with boot context for error correlation
 *
 * Redux Access Strategy:
 * Route error.tsx files render UNDER ReduxProvider (they wrap page content,
 * not layouts), so useAppSelector is safe and preferred over direct store access.
 * This avoids potential module singleton issues in Next.js App Router.
 */
const RouteError: React.FC<RouteErrorProps> = ({ error, reset }) => {
  const isProduction = config.env === 'production';

  // Get boot context via hooks - safe because error.tsx is under ReduxProvider
  const bootContext = useAppSelector(selectBootSentryContext);

  // Tag Sentry with boot context on mount (no-ops if Sentry.init() not called)
  useEffect(() => {
    if (bootContext.boot_id) {
      Sentry.setTag('boot_id', String(bootContext.boot_id));
    }
    if (bootContext.boot_status) {
      Sentry.setTag('boot_status', String(bootContext.boot_status));
    }
    if (bootContext.boot_route) {
      Sentry.setTag('boot_route', String(bootContext.boot_route));
    }

    Sentry.setExtra('boot_context', bootContext);

    Sentry.captureException(error, {
      tags: {
        error_boundary: 'route',
        boot_id: bootContext.boot_id,
        boot_status: bootContext.boot_status,
      },
      extra: {
        digest: error.digest,
        boot_context: bootContext,
      },
    });

    // Log to console in development
    if (!isProduction) {
      new Logger('app').error('[RouteError]', error);
      new Logger('app').info('[RouteError] Boot context:', bootContext);
    }
  }, [error, isProduction, bootContext]);

  const handleRetry = () => {
    reset();
  };

  const handleHardReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6" data-testid="route-error">
      <div className="max-w-md w-full text-center">
        {/* Error icon */}
        <div className="mb-6">
          <AlertCircle
            className="mx-auto h-12 w-12"
            style={{ color: 'var(--color-error, #EF4444)' }}
          />
        </div>

        {/* Title */}
        <h2
          className="text-xl font-semibold mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Something went wrong
        </h2>

        {/* Error message (production-safe) */}
        <p
          className="mb-2 text-sm"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {isProduction
            ? 'An unexpected error occurred. Please try again.'
            : error.message || 'An unexpected error occurred.'
          }
        </p>

        {/* Request ID / Digest (if available) */}
        {error.digest && (
          <p
            className="mb-6 text-xs font-mono"
            style={{ color: 'var(--color-text-tertiary, #9CA3AF)' }}
          >
            Error ID: {error.digest}
          </p>
        )}

        {/* Stack trace in development only */}
        {!isProduction && error.stack && (
          <details
            className="mb-6 text-left p-3 rounded-lg text-xs"
            style={{
              backgroundColor: 'var(--color-surface, #F3F4F6)',
              border: '1px solid var(--color-border)'
            }}
          >
            <summary
              className="cursor-pointer font-medium"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Stack trace
            </summary>
            <pre
              className="mt-2 overflow-auto whitespace-pre-wrap font-mono"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {error.stack}
            </pre>
          </details>
        )}

        {/* Action buttons */}
        <div className="flex justify-center gap-3">
          {/* Retry button - primary action */}
          <button
            onClick={handleRetry}
            className="px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            style={{
              backgroundColor: 'var(--color-primary)',
              color: 'white'
            }}
            data-testid="error-retry-button"
          >
            <RotateCcw className="h-4 w-4" />
            Retry
          </button>

          {/* Hard reload - secondary action for catastrophic states */}
          <button
            onClick={handleHardReload}
            className="px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            style={{
              backgroundColor: 'var(--color-surface, #F3F4F6)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)'
            }}
            data-testid="error-reload-button"
          >
            <RefreshCw className="h-4 w-4" />
            Hard Reload
          </button>
        </div>

        {/* Help text */}
        <p
          className="mt-4 text-xs"
          style={{ color: 'var(--color-text-tertiary, #9CA3AF)' }}
        >
          If the problem persists, try refreshing the page or contact support.
        </p>
      </div>
    </div>
  );
};

export default RouteError;
